import { NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, Component, input, signal, viewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { DatePicker } from "primeng/datepicker";
import { FloatLabel } from "primeng/floatlabel";
import { FormControlBaseDirective } from "../form-control-base.directive";

let nextId = 0;

const getDateFromString = (value: string): Date | undefined => {
    const digits = value
        .split(/\D+/)
        .filter(Boolean)
        .map((part) => part.padStart(2, "0"))
        .join("");

    if (digits.length < 6) return undefined;

    const month = +digits.slice(0, 2);
    const day = +digits.slice(2, 4);
    const year = digits.length >= 8 ? +digits.slice(4, 8) : 2000 + +digits.slice(4, 6);

    const date = new Date(year, month - 1, day);
    if (isNaN(date.getTime())) return undefined;

    if (date > new Date()) date.setFullYear(date.getFullYear() - 100);

    return date;
};

@Component({
    selector: "kno2-form-datepicker",
    imports: [FormsModule, DatePicker, FloatLabel, NgTemplateOutlet],
    templateUrl: "./form-datepicker.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormDatepickerComponent extends FormControlBaseDirective {
    public readonly inputId = input<string>(`kno2-form-datepicker-${nextId++}`);
    public readonly placeholder = input<string>();
    public readonly maxDate = input<Date>();
    public readonly size = input<"small" | "large">();

    protected readonly value = signal<Date | null>(null);
    protected readonly datePicker = viewChild.required<DatePicker>("datePicker");

    protected applyValue(value: unknown): void {
        this.value.set((value as Date) ?? null);
    }

    protected handleChange(value: Date | null): void {
        this.value.set(value);
        this.emitChange(value);
    }

    protected handleBlur(event: Event): void {
        this.markTouched();
        this.formatTypedValue(event);
    }

    protected formatTypedValue(event: Event): void {
        if (this.datePicker().overlayVisible) return;

        const date = getDateFromString((event.target as HTMLInputElement).value);
        if (date) this.handleChange(date);
    }

    protected formatPastedValue(event: ClipboardEvent): void {
        const date = getDateFromString(event.clipboardData?.getData("text/plain") ?? "");
        if (date) this.handleChange(date);
    }
}
